import {
    ProductType,
    RequestStatus,
} from '@/libs/product'
import { createRequestHandlingValidation } from './request-handling-validation'
import {
    RequestHandlingStrategy,
    createRequestHandlingWorkflowState,
    isRequestHandlingStrategyAllowed,
    requestDomainStateForStrategy,
} from './request-handling-strategy'

const DEFAULT_SUPPLIER_SAVE_DELAY = 1000
const DEFAULT_FORWARDED_REQUESTS_POLL_INTERVAL = 5000

const getService = (context, name) => {
    const service = context.services && context.services[name]

    if (typeof service !== 'function') {
        throw new Error(`Request Handling requires services.${name}()`)
    }

    return service
}

const createRequestHandlingController = ({
    context,
    step,
    setValidity = () => {},
    setDirty,
    setSaving,
    publishEffect,
    reportError,
    supplierSaveDelay = DEFAULT_SUPPLIER_SAVE_DELAY,
    forwardedRequestsPollInterval = DEFAULT_FORWARDED_REQUESTS_POLL_INTERVAL,
}) => {
    const settings = (step && step.settings) || {}
    const productStructureEnabled = Boolean(
        settings.showProductStructure === true,
    )
    const forwardedRequestsEnabled = !step
        || !step.settings
        || step.settings.showForwardedRequests !== false
    let validation = createRequestHandlingValidation(
        context,
        settings,
    )
    let pendingOperation = Promise.resolve()
    let strategyPending = false
    let supplierDetails = null
    let supplierRevision = 0
    let queuedSupplierRevision = 0
    let savedSupplierRevision = 0
    let supplierSaveTimeout = null
    let forwardedRequestsRefreshPromise = null
    let forwardedRequestsPollTimeout = null
    let workflowStateRefreshPromise = null
    let active = false
    let disposed = false

    const forwardedRequestsState = context.requestHandling
        && context.requestHandling.forwardedRequests
    const forwardModalState = context.requestHandling
        && context.requestHandling.forwardModal
    const workflowState = context.requestHandling
        && context.requestHandling.workflowState

    const refreshValidation = () => {
        validation = createRequestHandlingValidation(
            context,
            settings,
        )
        setValidity(validation)
    }

    const shouldLoadForwardedRequests = () => (
        forwardedRequestsEnabled
        && Boolean(forwardedRequestsState)
        && context.request
        && (
            context.request.status === RequestStatus.FORWARDED_TO_SUPPLIER
            || (
                workflowState
                && workflowState.strategy
                    === RequestHandlingStrategy.FORWARD_TO_PRODUCT_SUPPLIER
            )
        )
    )

    const refreshWorkflowState = () => {
        if (!workflowState || workflowStateRefreshPromise) {
            return workflowStateRefreshPromise || Promise.resolve(null)
        }

        workflowState.error = null
        workflowStateRefreshPromise = Promise.resolve()
            .then(() => getService(context, 'getReceivedRequestWorkflowState')())
            .then(response => {
                const document = response && response.data
                    ? response.data.workflowState
                    : null

                Object.assign(workflowState, {
                    schemaVersion: document ? document.schemaVersion : null,
                    strategy: document && document.requestHandling
                        ? document.requestHandling.strategy
                        : null,
                    loaded: true,
                    error: null,
                })

                return document
            })
            .catch(error => {
                workflowState.error = error
                workflowState.loaded = false
                throw error
            })
            .finally(() => {
                workflowStateRefreshPromise = null
            })

        return workflowStateRefreshPromise
    }

    const hasPendingForwardedRequests = () => (
        Boolean(forwardedRequestsState)
        && forwardedRequestsState.items.some(item => (
            item.forwardState === 'pending_request_creation'
        ))
    )

    const stopForwardedRequestsPolling = () => {
        clearTimeout(forwardedRequestsPollTimeout)
        forwardedRequestsPollTimeout = null
    }

    const scheduleForwardedRequestsPolling = () => {
        stopForwardedRequestsPolling()

        if (
            !active
            || disposed
            || !forwardedRequestsPollInterval
            || !hasPendingForwardedRequests()
        ) {
            return
        }

        forwardedRequestsPollTimeout = setTimeout(() => {
            forwardedRequestsPollTimeout = null
            refreshForwardedRequests()
                .catch(() => undefined)
                .finally(scheduleForwardedRequestsPolling)
        }, forwardedRequestsPollInterval)
    }

    function refreshForwardedRequests() {
        if (!forwardedRequestsState || forwardedRequestsRefreshPromise) {
            return forwardedRequestsRefreshPromise || Promise.resolve([])
        }

        if (!forwardedRequestsState.loaded) {
            forwardedRequestsState.loading = true
        }
        forwardedRequestsState.error = null

        forwardedRequestsRefreshPromise = Promise.resolve()
            .then(() => getService(context, 'getReceivedRequestForwardedRequests')())
            .then(response => {
                const items = response && response.data && Array.isArray(response.data.items)
                    ? response.data.items
                    : []

                forwardedRequestsState.items.splice(
                    0,
                    forwardedRequestsState.items.length,
                    ...items,
                )
                forwardedRequestsState.loaded = true

                return items
            })
            .catch(error => {
                forwardedRequestsState.error = error
                throw error
            })
            .finally(() => {
                forwardedRequestsState.loading = false
                forwardedRequestsRefreshPromise = null
            })

        return forwardedRequestsRefreshPromise
    }

    const normalizeForwardTarget = target => ({
        ...target,
        selected: !target.ongoingRequestDate,
        hasIdentifier: Boolean(target.supplierIdentifier),
        hasName: Boolean(target.supplierName),
        supplierIdentifier: target.supplierIdentifier || target.ourIdentifier || '',
        supplierName: target.supplierName || target.ourName || '',
    })

    const refreshForwardTargets = async () => {
        if (!forwardModalState) {
            throw new Error('Request Handling requires context.requestHandling.forwardModal')
        }

        forwardModalState.loading = true
        forwardModalState.error = null

        try {
            const response = await getService(context, 'getForwardRequestTargets')()
            const targets = response && Array.isArray(response.data) ? response.data : []

            forwardModalState.targets.splice(
                0,
                forwardModalState.targets.length,
                ...targets.map(normalizeForwardTarget),
            )
            forwardModalState.loaded = true

            return forwardModalState.targets
        } catch (error) {
            forwardModalState.error = error
            throw error
        } finally {
            forwardModalState.loading = false
        }
    }

    const submitForwardRequest = async ({
        request: forwardRequestData,
        successCallback,
        failureCallback,
    }) => {
        const selectedTargets = forwardModalState.targets.filter(target => target.selected === true)
        const invalidTargets = selectedTargets.filter(target => (
            !target.supplierIdentifier && !target.supplierName
        ))

        if (selectedTargets.length === 0 || invalidTargets.length > 0) {
            const error = new Error('Select at least one product with a supplier product name or ID.')
            if (typeof failureCallback === 'function') {
                failureCallback(error)
            }
            return Promise.reject(error)
        }

        forwardModalState.submitting = true
        forwardModalState.error = null
        setSaving(true)

        try {
            const targetsToSave = selectedTargets.filter(target => (
                target.hasIdentifier === false || target.hasName === false
            ))

            await Promise.all(targetsToSave.map(target => (
                getService(context, 'saveForwardRequestTargetIdentity')({
                    id: target.id,
                    name: target.supplierName,
                    identifier: target.supplierIdentifier,
                })
            )))

            await getService(context, 'submitReceivedRequestForward')({
                ...forwardRequestData,
                products: selectedTargets.map(target => Number(target.id)),
            })
            await Promise.resolve(publishEffect({
                type: 'request.forwarded.changed',
                phase: 'committed',
                scopes: ['request.details', 'request.forwarded-requests'],
            })).catch(() => undefined)
            await refreshForwardedRequests().catch(() => undefined)
            scheduleForwardedRequestsPolling()
            reportError(null)
        } catch (error) {
            forwardModalState.error = error
            reportError(error)
            if (typeof failureCallback === 'function') {
                failureCallback(error)
            }
            throw error
        } finally {
            forwardModalState.submitting = false
            setSaving(false)
        }

        if (typeof successCallback === 'function') {
            successCallback()
        }
    }

    const syncDirty = () => setDirty(
        strategyPending || savedSupplierRevision < supplierRevision,
    )

    const hasProductStructureComponents = () => {
        const hierarchy = context.productHierarchy || {}
        return Array.isArray(hierarchy.children) && hierarchy.children.length > 0
    }

    const productStructureMustBeResolved = () => (
        productStructureEnabled
        && context.request
        && (
            context.request.isComplexProduct === true
            || (
                workflowState
                && workflowState.strategy
                    === RequestHandlingStrategy.COLLECT_FROM_COMPONENT_SUPPLIERS
            )
            || (context.product && context.product.isComplex === true)
        )
    )

    const queueOperation = operation => {
        pendingOperation = pendingOperation
            .catch(() => undefined)
            .then(async () => {
                setSaving(true)
                try {
                    const result = await operation()
                    reportError(null)
                    return result
                } catch (error) {
                    reportError(error)
                    throw error
                } finally {
                    setSaving(false)
                    syncDirty()
                }
            })

        return pendingOperation
    }

    const saveStrategy = strategy => {
        if (!isRequestHandlingStrategyAllowed(strategy, context, settings)) {
            return Promise.reject(new Error(
                `Request handling strategy "${strategy}" is not available in "${context.mode}" mode.`,
            ))
        }

        const switchesToSimpleProduct = strategy
            !== RequestHandlingStrategy.COLLECT_FROM_COMPONENT_SUPPLIERS
        const productStructureUnresolved = productStructureMustBeResolved()
            && productStructureState
            && productStructureState.loaded !== true

        if (
            switchesToSimpleProduct
            && (hasProductStructureComponents() || productStructureUnresolved)
        ) {
            return Promise.reject(new Error(
                'Remove all product components before switching to a simple-product strategy.',
            ))
        }

        strategyPending = true
        syncDirty()

        return queueOperation(async () => {
            if (strategy === RequestHandlingStrategy.FORWARD_TO_PRODUCT_SUPPLIER) {
                const suppliers = context.product.suppliers || []
                const targetType = suppliers.length > 0
                    ? ProductType.PURCHASED
                    : ProductType.PURCHASED_UNDEFINED_SUPPLIER

                if (
                    context.product.type !== ProductType.PURCHASED
                    && context.product.type !== targetType
                ) {
                    await getService(context, 'convertProductType')(targetType)
                    await publishEffect({
                        type: 'product.card.changed',
                        phase: 'committed',
                        scopes: ['product.card'],
                    })
                }
            }

            await getService(context, 'changeRequestHandlingStrategy')(
                requestDomainStateForStrategy(strategy),
            )
            await publishEffect({
                type: 'request.details.changed',
                phase: 'committed',
                scopes: ['request.details'],
            })

            const document = createRequestHandlingWorkflowState(strategy)
            if (workflowState) {
                workflowState.saving = true
                workflowState.error = null
            }
            try {
                await getService(context, 'saveReceivedRequestWorkflowState')(document)
                if (workflowState) {
                    Object.assign(workflowState, {
                        schemaVersion: document.schemaVersion,
                        strategy,
                        loaded: true,
                        error: null,
                    })
                }
            } catch (error) {
                if (workflowState) {
                    workflowState.error = error
                }
                throw error
            } finally {
                if (workflowState) {
                    workflowState.saving = false
                }
            }
            await publishEffect({
                type: 'request.workflow-state.changed',
                phase: 'committed',
                scopes: ['request.workflow-state'],
            })
            strategyPending = false

            if (
                strategy === RequestHandlingStrategy.COLLECT_FROM_COMPONENT_SUPPLIERS
                && productStructureEnabled
            ) {
                await refreshProductStructure()
            }
        })
    }

    const productStructureState = context.requestHandling
        && context.requestHandling.productStructure

    const refreshProductStructure = async () => {
        if (!productStructureState) {
            throw new Error('Request Handling requires context.requestHandling.productStructure')
        }

        productStructureState.loading = true
        try {
            return await getService(context, 'refreshProductStructureHierarchy')()
        } finally {
            productStructureState.loading = false
        }
    }

    const ensureProductStructureLoaded = () => {
        if (!productStructureEnabled || !productStructureState) {
            return Promise.resolve()
        }

        if (context.productHierarchy && context.productHierarchy.id) {
            productStructureState.loaded = true
            return Promise.resolve(context.productHierarchy)
        }

        return refreshProductStructure()
    }

    const runProductStructureOperation = (serviceName, payload) => {
        const successCallback = payload.successCallback
        const failureCallback = payload.failureCallback

        if (payload.mode && payload.mode !== 'top-down') {
            const error = new Error(`Unsupported product structure mode "${payload.mode}"`)
            if (typeof failureCallback === 'function') {
                failureCallback(error)
            }
            return Promise.reject(error)
        }

        if (productStructureState) {
            productStructureState.busyNodeKey = payload.nodeKey || null
        }

        return queueOperation(async () => {
            try {
                await getService(context, serviceName)(payload)
                await refreshProductStructure()
                await publishEffect({
                    type: 'product.hierarchy.changed',
                    phase: 'committed',
                    scopes: ['product.hierarchy', 'product.card'],
                })
                if (typeof successCallback === 'function') {
                    successCallback()
                }
            } catch (error) {
                if (typeof failureCallback === 'function') {
                    failureCallback(error)
                }
                throw error
            } finally {
                if (productStructureState) {
                    productStructureState.busyNodeKey = null
                }
            }
        })
    }

    const saveSupplierDetails = () => {
        clearTimeout(supplierSaveTimeout)
        supplierSaveTimeout = null

        if (queuedSupplierRevision >= supplierRevision) {
            return pendingOperation
        }

        const revision = supplierRevision
        const details = {
            ...supplierDetails,
            suppliers: [...supplierDetails.suppliers],
        }
        queuedSupplierRevision = revision

        return queueOperation(async () => {
            try {
                await getService(context, 'saveProductSupplierDetails')(details)
                if (context.product.type !== ProductType.PURCHASED) {
                    await getService(context, 'convertProductType')(ProductType.PURCHASED)
                }
                await publishEffect({
                    type: 'product.card.changed',
                    phase: 'committed',
                    scopes: ['product.card'],
                })
                savedSupplierRevision = Math.max(savedSupplierRevision, revision)
            } catch (error) {
                queuedSupplierRevision = savedSupplierRevision
                throw error
            }
        })
    }

    const scheduleSupplierDetailsSave = details => {
        if (disposed) {
            return Promise.resolve()
        }

        supplierDetails = {
            ...details,
            suppliers: [...details.suppliers],
        }
        supplierRevision += 1
        syncDirty()
        clearTimeout(supplierSaveTimeout)
        supplierSaveTimeout = setTimeout(() => {
            saveSupplierDetails().catch(() => undefined)
        }, supplierSaveDelay)

        return Promise.resolve()
    }

    const flushSupplierDetails = async () => {
        clearTimeout(supplierSaveTimeout)
        supplierSaveTimeout = null
        await saveSupplierDetails()
        await pendingOperation

        if (savedSupplierRevision < supplierRevision) {
            await saveSupplierDetails()
        }
    }

    const prepareForwardModal = async () => {
        if (forwardModalState) {
            forwardModalState.loading = true
            forwardModalState.error = null
        }

        try {
            await flushSupplierDetails()
            return await refreshForwardTargets()
        } catch (error) {
            if (forwardModalState) {
                forwardModalState.error = error
            }
            throw error
        } finally {
            if (forwardModalState) {
                forwardModalState.loading = false
            }
        }
    }

    return Object.freeze({
        async load() {
            await refreshWorkflowState()
            const productStructurePromise = productStructureMustBeResolved()
                ? ensureProductStructureLoaded()
                : Promise.resolve()
            const forwardedRequestsPromise = shouldLoadForwardedRequests()
                ? refreshForwardedRequests().catch(() => undefined)
                : Promise.resolve()

            await Promise.all([productStructurePromise, forwardedRequestsPromise])
            refreshValidation()
        },
        activate() {
            active = true
            scheduleForwardedRequestsPolling()
        },
        deactivate() {
            active = false
            stopForwardedRequestsPolling()
        },
        setValidation(nextValidation) {
            validation = nextValidation
        },
        validate() {
            return Promise.resolve(validation)
        },
        handleAction(action) {
            if (action.type === 'request-handling.change') {
                return saveStrategy(action.payload.strategy)
            }

            if (action.type === 'request-handling.supplier-details.change') {
                return scheduleSupplierDetailsSave(action.payload)
            }

            if (action.type === 'request-handling.forward') {
                return prepareForwardModal()
            }

            if (action.type === 'request-handling.forward.submit') {
                return submitForwardRequest(action.payload)
            }

            if (action.type === 'request-handling.forwarded-requests.refresh') {
                return refreshForwardedRequests()
                    .catch(() => undefined)
                    .finally(scheduleForwardedRequestsPolling)
            }

            if (action.type === 'request-handling.forwarded-request.open') {
                return getService(context, 'openSentRequest')(action.payload.request)
            }

            if (action.type === 'request-handling.product-structure.add') {
                return runProductStructureOperation(
                    'createProductStructureComponent',
                    action.payload,
                )
            }

            if (action.type === 'request-handling.product-structure.update') {
                return runProductStructureOperation(
                    'updateProductStructureComponent',
                    action.payload,
                )
            }

            if (action.type === 'request-handling.product-structure.remove') {
                return runProductStructureOperation(
                    'removeProductStructureComponent',
                    action.payload,
                )
            }

            throw new Error(`Unsupported Request Handling action "${action.type}"`)
        },
        async flush() {
            await flushSupplierDetails()
            await (forwardedRequestsRefreshPromise || Promise.resolve())
        },
        dispose() {
            disposed = true
            active = false
            clearTimeout(supplierSaveTimeout)
            supplierSaveTimeout = null
            stopForwardedRequestsPolling()
        },
    })
}

export { createRequestHandlingController }
export default createRequestHandlingController
